import api from '../../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiInbox } from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import { VscClearAll } from 'react-icons/vsc'

import Input from "../../../shared/components/form/input"
import useMyPermission from '../../../hooks/useMyPermission'
import ListServiceOrderOpenRow from './ListRow/ListServiceOrderOpenRow'
import ServiceOrderOpenFilterModal from './ServiceOrderOpenFilterModal'
import '../ServiceOrder/ServiceOrder.css'

const OPEN_STATUSES = ['aberta', 'em_andamento']
const EMPTY_FILTERS = { client: null, generator: null, technician: null, dateFrom: '', dateTo: '' }

function ServiceOrderOpen() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [serviceOrders, setServiceOrders] = useState([])
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [modalFilter, setModalFilter] = useState(false)
    const [load, setLoad] = useState(false)

    const [myPermission, setMyPermission] = useState({ group: "" })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, "serviceOrderOpen_View")

    const getAll = () => {
        api.get(`/serviceOrder/getall`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response) {
                setServiceOrders(response.data.serviceOrders)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => { getAll() }, [token])

    const openServiceOrders = useMemo(
        () => serviceOrders
            .filter((item) => OPEN_STATUSES.includes(item.status?.value))
            .sort((a, b) => {
                const keyA = `${a.scheduledDate || '9999-99-99'} ${a.scheduledStartTime || '99:99'}`
                const keyB = `${b.scheduledDate || '9999-99-99'} ${b.scheduledStartTime || '99:99'}`
                return keyA.localeCompare(keyB)
            }),
        [serviceOrders]
    )

    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return openServiceOrders.filter((item) => {
            const matchSearch =
                (item.name || '').toLowerCase().includes(searchLowerCase) ||
                (item.code || '').toLowerCase().includes(searchLowerCase)
            const matchClient = !filters.client || item.client?._id === filters.client._id || item.client?.value === filters.client.value
            const matchGenerator = !filters.generator || item.generator?._id === filters.generator._id || item.generator?.value === filters.generator.value
            const matchTechnician = !filters.technician || (item.technicians || []).some(
                (t) => (t._id || t.value) === (filters.technician._id || filters.technician.value)
            )
            const matchDateFrom = !filters.dateFrom || (item.scheduledDate && item.scheduledDate >= filters.dateFrom)
            const matchDateTo = !filters.dateTo || (item.scheduledDate && item.scheduledDate <= filters.dateTo)
            return matchSearch && matchClient && matchGenerator && matchTechnician && matchDateFrom && matchDateTo
        })
    }, [openServiceOrders, search, filters])

    const hasActiveFilter = !!search
        || !!filters.client
        || !!filters.generator
        || !!filters.technician
        || !!filters.dateFrom
        || !!filters.dateTo

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando ordens de serviço...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <div className="pg-title-row">
                            <p className="pg-title">Ordem de Serviço</p>
                            <span className="pg-title-tag so-title-tag-blue">Em Aberto</span>
                        </div>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions" />
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar ordem de serviço..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'ordem' : 'ordens'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'ordem encontrada' : 'ordens encontradas'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListServiceOrderOpenRow
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermission={myPermission}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma ordem de serviço em aberto</p>
                        <p className="pg-empty-subtitle">
                            {openServiceOrders.length === 0
                                ? 'As OS abertas ou em andamento aparecem aqui.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            {modalFilter && (
                <ServiceOrderOpenFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    serviceOrders={openServiceOrders}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar ordens de serviço</p>
        </div>
    )
}

export default ServiceOrderOpen
